import { useT } from "@transifex/react";
import { useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import { ClassActivityCategory } from "../../../constants/ClassActivityConstants";
import { useGeneralContext } from "../../../context/AppGeneralContext";
import { ClassActivityList } from "../../classActivityCard/ClassActivityList";
import { ClassActivityPopups } from "../../classActivityCard/ClassActivityPopups";
import type { ClassActivity } from "../../classActivityCard/ClassActivityCardsOverview.types";
import { useClassActivityData } from "../../../hooks/useClassActivityData";
import useBreakpoints, { Breakpoint } from "../../../hooks/useBreakpoints";
import useToast from "../../../hooks/useToast";
import { AreaButton, ButtonVariant, IconPosition } from "../../teacherArea/AreaButton";
import { CursorClickIcon } from "@phosphor-icons/react";
import { PhosphorIcon } from "../../../basic/phosphorIcon/PhosphorIcon";
import { CreatePopup } from "../../../basic/popups/CreatePopup";
import ToastMessage from "../../../basic/toastMessage/ToastMessage";
import { ClassDetailsFilterBar, ClassDetailsSortByOptions, ActivityVisibilityFilter } from "./ClassDetailsFilterBar";
import SelectExistingActivityPopup from "../popups/SelectExistingActivityPopup";
import ClassMemberViewPopup from "./ClassMemberViewPopup";
import { getClassMemberPreviewLoginRoute, getClassroomPreviewRoute } from "../../../helpers/schoolClassHelpers";
import type { SchoolClassVisibilities } from "../../../enums/schoolClasses";
import { isPrivateSchoolClass } from "../../../utils/schoolClassUtils";
import type { ClassMember } from "../../../networking/schoolClasses";

interface ClassActivitiesSectionProps {
    schoolClassId: string;
    schoolClassName: string;
    classVisibility: SchoolClassVisibilities;
    classMembers: ClassMember[];
    areClassMembersLoaded: boolean;
    hasClassMembersLoadError: boolean;
    onAddClassMembers: () => void;
}

const ClassActivitiesSection = (props: ClassActivitiesSectionProps) => {
    const t = useT();
    const history = useHistory();
    const { getStyleBasedOnBreakpoint } = useBreakpoints();

    // While a private class is confirmed to have no students, creating them is the screen's primary
    // action and adding activities steps back to secondary. An unloaded list stays primary so the
    // button doesn't flicker while the members fetch runs.
    const isAddActivityPrimary =
        !isPrivateSchoolClass(props.classVisibility) || !props.areClassMembersLoaded || props.classMembers.length > 0;

    const t_classroom = t("Classroom", { _tags: "SchoolClasses, ClassActivities" });
    const t_classMemberView = t("Student view", { _tags: "SchoolClasses, ClassActivities" });
    const t_newActivity = t("New Activity", { _tags: "SchoolClasses, ClassActivities" });
    const t_emptyState = t("No activities yet. Add your first one now to share it with the class.", {
        _tags: "SchoolClasses, ClassActivities",
    });
    const t_noFilterResults = t("No results matching the selected filter criteria", {
        _tags: "ClassActivity, ActivityFilterBar",
    });
    const t_vocabTrainingsLoadError = t("Vocab Trainings couldn't be loaded. Please try again later.", {
        _tags: "ClassActivity, ActivityList",
    });
    const t_userTestsLoadError = t("Tests couldn't be loaded. Please try again later.", {
        _tags: "ClassActivity, ActivityList",
    });
    const t_activitiesLoadError = t("Activities couldn't be loaded. Please try again later.", {
        _tags: "ClassActivity, ActivityList",
    });
    const t_activityAdded = t("Activity successfully added!", {
        _tags: "SchoolClasses, ClassActivities",
    });
    const t_activitiesAdded = (count: number) =>
        t("{count} activities successfully added!", {
            _tags: "SchoolClasses, ClassActivities",
            count,
        });
    const t_whatToAdd = t("What type of activity would you like to add?", {
        _tags: "SchoolClasses, ClassActivities",
    });
    const t_or = t("OR", { _tags: "SchoolClasses, ClassActivities" });
    const t_selectExisting = t("Select existing activity", { _tags: "SchoolClasses, ClassActivities" });

    const t_vocabularyFrom = (source: string) => t("Vocabulary from {source}", { _tags: "AssistantOverview", source });
    const t_oclock = t("o'clock", { _tags: "AssistantOverview" });
    const t_lastModifiedOn = t("Last modified on:", { _tags: "AssistantOverview" });
    const t_createdOn = t("Created on:", { _tags: "AssistantOverview" });

    const t_deleteVocabTraining = t("Delete assigned vocabulary", { _tags: "AssistantOverview" });
    const t_deleteVocabTrainingConfirmation = t("Do you really want to delete <b>your assigned vocabulary</b>?", {
        _tags: "AssistantOverview",
    });
    const t_deleteTask = t("Delete Task", { _tags: "ClassActivity, ClassActivityPopup" });
    const t_deleteTaskConfirmation = (taskName: string) =>
        t("Do you really want to delete <b>{taskName}</b>?", {
            _tags: "ClassActivity, ClassActivityPopup",
            taskName,
        });
    const t_deleteConfirmationNote = t("All previous answers and evaluations will also be deleted.", {
        _tags: "ClassActivity, ClassActivityPopup",
    });
    const t_duplicateTask = t("Duplicate Task", { _tags: "ClassActivity, ClassActivityPopup" });
    const t_duplicateVocabTraining = t("Duplicate vocabulary training", {
        _tags: "ClassActivity, ClassActivityPopup",
    });
    const t_taskName = t("Task name", { _tags: "ClassActivity, ClassActivityPopup" });
    const t_vocabTrainingName = t("Vocabulary training name", { _tags: "ClassActivity, ClassActivityPopup" });
    const t_sr_duplicate = t("Duplicate", { _tags: "ClassActivity, ScreenReaderOnly" });
    const t_copyOf = (name: string) => t("Copy of {name}", { _tags: "ClassActivity, ClassActivityPopup", name });
    const t_assignCopyToClass = (schoolClassName: string) =>
        t("Assign copy to class <b>{className}</b>", {
            _tags: "ClassActivity, ClassActivityPopup",
            className: schoolClassName,
        });
    const t_assignCopyToSameClass = t("Assign copy to the same class", { _tags: "ClassActivity, ClassActivityPopup" });

    const { setCreatingForSchoolClassId } = useGeneralContext();
    const {
        activities: allActivities,
        allDataLoaded,
        testOps,
        vocabTrainingOps,
        schoolClasses,
        studentTestContentWrapper,
        userTestsLoadError,
    } = useClassActivityData(props.schoolClassId);

    const [isNewActivityPopupOpen, setIsNewActivityPopupOpen] = useState(false);
    const [isSelectExistingOpen, setIsSelectExistingOpen] = useState(false);
    const [isClassMemberViewPopupOpen, setIsClassMemberViewPopupOpen] = useState(false);
    const [toastMessage, setToastMessage] = useState("");
    const activityToast = useToast();
    const [activityFilter, setActivityFilter] = useState("");
    const [visibilityFilter, setVisibilityFilter] = useState("");
    const [sortBy, setSortBy] = useState<ClassDetailsSortByOptions>(ClassDetailsSortByOptions.CREATED_DESC);

    const activities = useMemo((): ClassActivity[] => {
        return allActivities.filter((activity) => {
            if (activity.category === ClassActivityCategory.TEST) {
                return activity.testData.groupId === props.schoolClassId;
            }
            if (activity.category === ClassActivityCategory.VOCAB_TRAINING) {
                return activity.vocabTrainingData.groupId === props.schoolClassId;
            }
            return false;
        });
    }, [allActivities, props.schoolClassId]);

    const filteredActivities = useMemo((): ClassActivity[] => {
        const isActivityShared = (activity: ClassActivity): boolean => {
            if (activity.category === ClassActivityCategory.TEST) {
                return studentTestContentWrapper.hasOwnProperty(activity.id);
            }
            if (activity.category === ClassActivityCategory.VOCAB_TRAINING) {
                return !!activity.vocabTrainingData.shareLink;
            }
            return false;
        };

        return activities
            .filter((a) => {
                if (activityFilter && a.category !== activityFilter) return false;
                if (visibilityFilter) {
                    const shared = isActivityShared(a);
                    if (visibilityFilter === ActivityVisibilityFilter.SHARED && !shared) return false;
                    if (visibilityFilter === ActivityVisibilityFilter.NOT_SHARED && shared) return false;
                }
                return true;
            })
            .sort((a, b) =>
                sortBy === ClassDetailsSortByOptions.CREATED_ASC ? a.sortDate - b.sortDate : b.sortDate - a.sortDate
            );
    }, [activities, activityFilter, visibilityFilter, sortBy, studentTestContentWrapper]);

    const hasActiveFilters = activityFilter !== "" || visibilityFilter !== "";

    const showToast = (message: string) => {
        setToastMessage(message);
        activityToast.triggerToast();
    };

    const handleAssignComplete = (count: number) => {
        const message = count === 1 ? t_activityAdded : t_activitiesAdded(count);
        showToast(message);
    };

    const handleBackToPopup = () => {
        setIsSelectExistingOpen(false);
        setIsNewActivityPopupOpen(true);
    };

    // Private classes need a student to impersonate first; public classes can be previewed directly.
    const handleClassMemberViewClick = () => {
        if (isPrivateSchoolClass(props.classVisibility)) {
            setIsClassMemberViewPopupOpen(true);
            return;
        }
        history.push(getClassroomPreviewRoute(props.schoolClassId));
    };

    // The silent-login URL creates a real session for the selected student, so the classroom shows
    // their actual data; its returnTo carries the preview flag across the login redirect.
    const handleSelectClassMember = (classMember: ClassMember) => {
        setIsClassMemberViewPopupOpen(false);
        history.push(getClassMemberPreviewLoginRoute(props.schoolClassId, classMember.loginCode));
    };

    const handleAddClassMembersClick = () => {
        setIsClassMemberViewPopupOpen(false);
        props.onAddClassMembers();
    };

    return (
        <div
            className={`flex flex-col items-center bg-white w-full ${getStyleBasedOnBreakpoint(
                Breakpoint.MD,
                "px-l py-xl",
                "px-m py-xl"
            )}`}
            data-testid="class-activities-section"
            data-loaded={allDataLoaded}
        >
            <div
                className="flex flex-col gap-m w-full"
                style={{ maxWidth: `${process.env.REACT_APP_UPPER_GRID_STEP_BORDER}px` }}
            >
                <div className="flex flex-col gap-l">
                    <h2 className="text-xl font-semibold text-p6-grey-dark">{t_classroom}</h2>

                    <div
                        className={`flex gap-s ${getStyleBasedOnBreakpoint(
                            Breakpoint.MD,
                            "flex-row items-center justify-between",
                            "flex-col items-start"
                        )}`}
                    >
                        <AreaButton
                            data-testid="button-class-member-view"
                            variant={ButtonVariant.SECONDARY}
                            role="button"
                            iconName="preview"
                            iconPosition={IconPosition.RIGHT}
                            onClick={handleClassMemberViewClick}
                        >
                            {t_classMemberView}
                        </AreaButton>

                        <AreaButton
                            data-testid="button-new-activity"
                            variant={isAddActivityPrimary ? ButtonVariant.PRIMARY : ButtonVariant.SECONDARY}
                            role="button"
                            iconName="add"
                            onClick={() => setIsNewActivityPopupOpen(true)}
                        >
                            {t_newActivity}
                        </AreaButton>
                    </div>
                </div>

                {allDataLoaded && activities.length === 0 && (
                    <div className="min-h-[20vh] flex items-center justify-center">
                        <p className="text-center text-l text-p6-grey-mid">{t_emptyState}</p>
                    </div>
                )}

                {activities.length > 0 && (
                    <>
                        <ClassDetailsFilterBar
                            activityFilter={activityFilter}
                            onActivityFilterChange={setActivityFilter}
                            visibilityFilter={visibilityFilter}
                            onVisibilityFilterChange={setVisibilityFilter}
                            sortBy={sortBy}
                            onSortByChange={setSortBy}
                        />

                        {filteredActivities.length === 0 && hasActiveFilters ? (
                            <div className="min-h-[20vh] flex items-center justify-center">
                                <p className="text-center text-xl">{t_noFilterResults}</p>
                            </div>
                        ) : (
                            <ClassActivityList
                                activities={filteredActivities}
                                schoolClasses={schoolClasses}
                                studentTestContentWrapper={studentTestContentWrapper}
                                classFilter={props.schoolClassId}
                                languageFilter=""
                                testOps={testOps}
                                vocabTrainingOps={vocabTrainingOps}
                                onShareVocabTraining={vocabTrainingOps.handleShareButtonClick}
                                vocabTrainingsLoadError={vocabTrainingOps.vocabTrainingsLoadError}
                                userTestsLoadError={userTestsLoadError}
                                translations={{
                                    vocabularyFrom: t_vocabularyFrom,
                                    oclock: t_oclock,
                                    lastModifiedOn: t_lastModifiedOn,
                                    createdOn: t_createdOn,
                                    noFilterResults: t_noFilterResults,
                                    vocabTrainingsLoadError: t_vocabTrainingsLoadError,
                                    userTestsLoadError: t_userTestsLoadError,
                                    activitiesLoadError: t_activitiesLoadError,
                                }}
                            />
                        )}
                    </>
                )}

                <ClassActivityPopups
                    testOps={testOps}
                    vocabTrainingOps={vocabTrainingOps}
                    schoolClasses={schoolClasses}
                    studentTestContentWrapper={studentTestContentWrapper}
                    translations={{
                        deleteVocabTraining: t_deleteVocabTraining,
                        deleteVocabTrainingConfirmation: t_deleteVocabTrainingConfirmation,
                        deleteTask: t_deleteTask,
                        deleteTaskConfirmation: t_deleteTaskConfirmation,
                        deleteConfirmationNote: t_deleteConfirmationNote,
                        duplicateTask: t_duplicateTask,
                        duplicateVocabTraining: t_duplicateVocabTraining,
                        duplicate: t_sr_duplicate,
                        taskName: t_taskName,
                        vocabTrainingName: t_vocabTrainingName,
                        copyOf: t_copyOf,
                        assignCopyToClass: t_assignCopyToClass,
                        assignCopyToSameClass: t_assignCopyToSameClass,
                    }}
                />

                <CreatePopup
                    isOpen={isNewActivityPopupOpen}
                    onClose={() => setIsNewActivityPopupOpen(false)}
                    title={t_whatToAdd}
                    onOptionSelected={() => setCreatingForSchoolClassId(props.schoolClassId)}
                >
                    <div className="flex items-center gap-m">
                        <div className="flex-1 h-px bg-p6-grey-dark" />
                        <span className="text-l text-p6-grey-dark">{t_or}</span>
                        <div className="flex-1 h-px bg-p6-grey-dark" />
                    </div>

                    <button
                        type="button"
                        data-testid="button-select-existing-activity"
                        onClick={() => {
                            setIsNewActivityPopupOpen(false);
                            setIsSelectExistingOpen(true);
                        }}
                        className="w-full border-2 border-p6-grey-dark rounded-medium p-s flex items-center justify-center gap-s bg-white hover:brightness-95 cursor-pointer"
                    >
                        <PhosphorIcon
                            phosphorIcon={CursorClickIcon}
                            color="text-p6-grey-dark"
                            fontSize="text-xxl"
                        />
                        <span className="text-xl text-p6-grey-dark">{t_selectExisting}</span>
                    </button>
                </CreatePopup>

                {isClassMemberViewPopupOpen && (
                    <ClassMemberViewPopup
                        isOpen
                        onClose={() => setIsClassMemberViewPopupOpen(false)}
                        classMembers={props.classMembers}
                        hasLoadError={props.hasClassMembersLoadError}
                        onSelectClassMember={handleSelectClassMember}
                        onAddClassMembers={handleAddClassMembersClick}
                    />
                )}

                <SelectExistingActivityPopup
                    isOpen={isSelectExistingOpen}
                    onClose={() => setIsSelectExistingOpen(false)}
                    onBack={handleBackToPopup}
                    schoolClassId={props.schoolClassId}
                    schoolClassName={props.schoolClassName}
                    classVisibility={props.classVisibility}
                    onAssignComplete={handleAssignComplete}
                    vocabTrainings={vocabTrainingOps.vocabTrainings}
                    reloadVocabTrainings={vocabTrainingOps.loadVocabTrainings}
                />

                <ToastMessage
                    isOpen={activityToast.isOpen}
                    onOpenChange={activityToast.onOpenChange}
                    text={toastMessage}
                />
            </div>
        </div>
    );
};

export default ClassActivitiesSection;
